import { useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import { Button, DatePicker, Input, Select, Toggle } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import { WorkspaceCompanyChips, type WorkspaceCompanyFilter } from '@/features/companies/components/WorkspaceCompanyFilter'
import { cn } from '@/lib/cn'
import type {
  ComplianceReportQuery,
  ComplianceReportSortBy,
} from '@/features/reports/types/compliance-report.types'

type ComplianceReportFilterKey =
  | 'search'
  | 'companyStatus'
  | 'reportStatus'
  | 'hasOpenIssues'
  | 'minComplianceScore'
  | 'maxComplianceScore'
  | 'dateFrom'
  | 'dateTo'
  | 'sortBy'
  | 'sortOrder'

const COMPANY_STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All company statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

const REPORT_STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All report statuses' },
  { value: 'compliant', label: 'Compliant' },
  { value: 'needs_attention', label: 'Needs attention' },
  { value: 'at_risk', label: 'At risk' },
]

const SORT_BY_OPTIONS: SelectOption[] = [
  { value: 'complianceScore', label: 'Compliance score' },
  { value: 'companyName', label: 'Company name' },
  { value: 'activeMembers', label: 'Active members' },
  { value: 'pendingApprovals', label: 'Pending approvals' },
  { value: 'openPayrollRuns', label: 'Open payroll runs' },
  { value: 'payrollRunsWithErrors', label: 'Payroll errors' },
  { value: 'auditEvents', label: 'Audit events' },
  { value: 'lastAuditAt', label: 'Last audit' },
  { value: 'totalDocuments', label: 'Documents' },
]

const SORT_ORDER_OPTIONS: SelectOption[] = [
  { value: 'desc', label: 'Descending' },
  { value: 'asc', label: 'Ascending' },
]

function formatDateLabel(value: Date | null): string {
  if (!value) return ''
  return value.toLocaleDateString()
}

function activeFilterChips(
  params: ComplianceReportQuery
): Array<{ key: ComplianceReportFilterKey; label: string }> {
  const chips: Array<{ key: ComplianceReportFilterKey; label: string }> = []

  if (params.search) chips.push({ key: 'search', label: `Search: ${params.search}` })
  if (params.companyStatus) {
    chips.push({ key: 'companyStatus', label: `Company: ${params.companyStatus}` })
  }
  if (params.reportStatus) {
    chips.push({
      key: 'reportStatus',
      label: `Report: ${params.reportStatus.replace(/_/g, ' ')}`,
    })
  }
  if (params.hasOpenIssues) chips.push({ key: 'hasOpenIssues', label: 'Open issues only' })
  if (params.minComplianceScore != null) {
    chips.push({ key: 'minComplianceScore', label: `Min score: ${params.minComplianceScore}` })
  }
  if (params.maxComplianceScore != null) {
    chips.push({ key: 'maxComplianceScore', label: `Max score: ${params.maxComplianceScore}` })
  }
  if (params.dateFrom) {
    chips.push({
      key: 'dateFrom',
      label: `From: ${formatDateLabel(new Date(params.dateFrom))}`,
    })
  }
  if (params.dateTo) {
    chips.push({
      key: 'dateTo',
      label: `To: ${formatDateLabel(new Date(params.dateTo))}`,
    })
  }
  if (params.sortBy && params.sortBy !== 'complianceScore') {
    const match = SORT_BY_OPTIONS.find((option) => option.value === params.sortBy)
    chips.push({ key: 'sortBy', label: `Sort: ${match?.label ?? params.sortBy}` })
  }
  if (params.sortOrder && params.sortOrder !== 'desc') {
    chips.push({ key: 'sortOrder', label: 'Order: ascending' })
  }

  return chips
}

export interface ComplianceReportFiltersProps {
  params: ComplianceReportQuery
  companyFilter: WorkspaceCompanyFilter
  onApply: (query: ComplianceReportQuery) => void
  onReset: () => void
}

export function ComplianceReportFilters({
  params,
  companyFilter,
  onApply,
  onReset,
}: ComplianceReportFiltersProps): React.ReactElement {
  const [search, setSearch] = useState(params.search ?? '')
  const [companyStatus, setCompanyStatus] = useState(params.companyStatus ?? '')
  const [reportStatus, setReportStatus] = useState(params.reportStatus ?? '')
  const [hasOpenIssues, setHasOpenIssues] = useState(params.hasOpenIssues ?? false)
  const [minComplianceScore, setMinComplianceScore] = useState(
    params.minComplianceScore != null ? String(params.minComplianceScore) : ''
  )
  const [maxComplianceScore, setMaxComplianceScore] = useState(
    params.maxComplianceScore != null ? String(params.maxComplianceScore) : ''
  )
  const [dateFrom, setDateFrom] = useState<Date | null>(
    params.dateFrom ? new Date(params.dateFrom) : null
  )
  const [dateTo, setDateTo] = useState<Date | null>(
    params.dateTo ? new Date(params.dateTo) : null
  )
  const [sortBy, setSortBy] = useState<ComplianceReportSortBy>(
    params.sortBy ?? 'complianceScore'
  )
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(params.sortOrder ?? 'desc')

  const chips = useMemo(() => activeFilterChips(params), [params])

  const handleApply = (): void => {
    const next: ComplianceReportQuery = {
      page: 1,
      limit: params.limit ?? 10,
      sortBy,
      sortOrder,
      companyIds: companyFilter.companyIds,
    }

    if (search.trim()) next.search = search.trim()
    if (companyStatus) {
      next.companyStatus = companyStatus as ComplianceReportQuery['companyStatus']
    }
    if (reportStatus) {
      next.reportStatus = reportStatus as ComplianceReportQuery['reportStatus']
    }
    if (hasOpenIssues) next.hasOpenIssues = true

    const min = minComplianceScore.trim()
    const max = maxComplianceScore.trim()
    if (min !== '') next.minComplianceScore = Number(min)
    if (max !== '') next.maxComplianceScore = Number(max)
    if (dateFrom) next.dateFrom = `${dateFrom.toISOString().slice(0, 10)}T00:00:00.000Z`
    if (dateTo) next.dateTo = `${dateTo.toISOString().slice(0, 10)}T23:59:59.999Z`

    onApply(next)
  }

  const handleReset = (): void => {
    setSearch('')
    setCompanyStatus('')
    setReportStatus('')
    setHasOpenIssues(false)
    setMinComplianceScore('')
    setMaxComplianceScore('')
    companyFilter.reset()
    onReset()
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Companies
          </span>
          {companyFilter.companyIds && companyFilter.companyIds.length > 0 && (
            <button
              type="button"
              className="text-xs font-medium text-primary hover:opacity-80"
              onClick={companyFilter.reset}
            >
              Clear companies
            </button>
          )}
        </div>
        <WorkspaceCompanyChips filter={companyFilter} />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Input
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Company name, code, or slug"
          className="text-sm"
        />
        <Select
          id="reports-company-status"
          label="Company status"
          options={COMPANY_STATUS_OPTIONS}
          value={companyStatus}
          onChange={setCompanyStatus}
        />
        <Select
          id="reports-status"
          label="Report status"
          options={REPORT_STATUS_OPTIONS}
          value={reportStatus}
          onChange={setReportStatus}
        />
        <div className="flex items-end pb-1">
          <Toggle
            id="reports-open-issues"
            label="Has open issues"
            checked={hasOpenIssues}
            onChange={setHasOpenIssues}
          />
        </div>

        <Input
          label="Min score"
          type="number"
          min={0}
          max={100}
          value={minComplianceScore}
          onChange={(e) => setMinComplianceScore(e.target.value)}
          placeholder="0"
          className="text-sm"
        />
        <Input
          label="Max score"
          type="number"
          min={0}
          max={100}
          value={maxComplianceScore}
          onChange={(e) => setMaxComplianceScore(e.target.value)}
          placeholder="100"
          className="text-sm"
        />
        <DatePicker
          id="reports-date-from"
          label="From date"
          value={dateFrom}
          onChange={setDateFrom}
        />
        <DatePicker
          id="reports-date-to"
          label="To date"
          value={dateTo}
          onChange={setDateTo}
        />

        <Select
          id="reports-sort-by"
          label="Sort by"
          options={SORT_BY_OPTIONS}
          value={sortBy}
          onChange={(value) => setSortBy(value as ComplianceReportSortBy)}
          clearable={false}
        />
        <Select
          id="reports-sort-order"
          label="Sort order"
          options={SORT_ORDER_OPTIONS}
          value={sortOrder}
          onChange={(value) => setSortOrder(value as 'asc' | 'desc')}
          clearable={false}
        />
        <div className="flex items-end gap-2 xl:col-span-2">
          <Button type="button" variant="primary" onClick={handleApply}>
            <Search className="mr-2 h-4 w-4" />
            Apply filters
          </Button>
          <Button type="button" variant="secondary" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <span
              key={`${chip.key}-${chip.label}`}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700',
                'dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
              )}
            >
              {chip.label}
              <button
                type="button"
                className="rounded-full p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => {
                  const next: ComplianceReportQuery = {
                    ...params,
                    page: 1,
                    companyIds: companyFilter.companyIds,
                  }
                  switch (chip.key) {
                    case 'search':
                      setSearch('')
                      delete next.search
                      break
                    case 'companyStatus':
                      setCompanyStatus('')
                      delete next.companyStatus
                      break
                    case 'reportStatus':
                      setReportStatus('')
                      delete next.reportStatus
                      break
                    case 'hasOpenIssues':
                      setHasOpenIssues(false)
                      delete next.hasOpenIssues
                      break
                    case 'minComplianceScore':
                      setMinComplianceScore('')
                      delete next.minComplianceScore
                      break
                    case 'maxComplianceScore':
                      setMaxComplianceScore('')
                      delete next.maxComplianceScore
                      break
                    case 'dateFrom':
                      setDateFrom(null)
                      delete next.dateFrom
                      break
                    case 'dateTo':
                      setDateTo(null)
                      delete next.dateTo
                      break
                    case 'sortBy':
                      setSortBy('complianceScore')
                      next.sortBy = 'complianceScore'
                      break
                    case 'sortOrder':
                      setSortOrder('desc')
                      next.sortOrder = 'desc'
                      break
                  }
                  onApply(next)
                }}
                aria-label={`Remove ${chip.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

ComplianceReportFilters.displayName = 'ComplianceReportFilters'
